/**
 * 1976 standard atmosphere, troposphere + lower stratosphere. AD-007c makes the
 * density lapse a flagged subsystem (default on) because it is what lets the
 * per-aircraft service ceilings stored in `<PLANE>.DAT` emerge from the physics
 * instead of being clamped — see aircraft-spec.ts, which solves each aircraft's
 * power lapse against its stored ceiling.
 */

import { SEA_LEVEL_DENSITY_KGM3 } from "./units.ts";

const TROPOPAUSE_M = 11000;
const SEA_LEVEL_TEMPERATURE_K = 288.15;
const TROPOSPHERE_LAPSE_K_PER_M = 0.0065;
const TROPOPAUSE_TEMPERATURE_K = 216.65;
const TROPOPAUSE_DENSITY_KGM3 = 0.363918;
const STRATOSPHERE_SCALE_HEIGHT_M = 6341.62;

export interface AirProperties {
  readonly densityKgM3: number;
  /** Density as a fraction of sea level — the σ that both drag and the engine lapse key off. */
  readonly densityRatio: number;
  readonly temperatureK: number;
}

export function standardAtmosphere(altitudeM: number): AirProperties {
  if (altitudeM < TROPOPAUSE_M) {
    const temperatureK = SEA_LEVEL_TEMPERATURE_K - TROPOSPHERE_LAPSE_K_PER_M * altitudeM;
    const densityRatio = Math.pow(1 - 2.25577e-5 * altitudeM, 4.25588);
    return { densityKgM3: SEA_LEVEL_DENSITY_KGM3 * densityRatio, densityRatio, temperatureK };
  }
  const densityKgM3 =
    TROPOPAUSE_DENSITY_KGM3 * Math.exp(-(altitudeM - TROPOPAUSE_M) / STRATOSPHERE_SCALE_HEIGHT_M);
  return {
    densityKgM3,
    densityRatio: densityKgM3 / SEA_LEVEL_DENSITY_KGM3,
    temperatureK: TROPOPAUSE_TEMPERATURE_K,
  };
}

/** Density ratio σ alone, for the engine lapse and the ceiling solver. */
export function densityRatioAt(altitudeM: number): number {
  return standardAtmosphere(altitudeM).densityRatio;
}
